import { isNil, isNotNil } from "@etudescarre/utils";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";

type MatrixData = {
  lots: Array<{
    id: number;
    nLot: string | null;
    shortName: string | null;
    name: string;
    performances: Array<{ id: number; name: string }>;
  }>;
  buildings: Array<{
    id: number;
    shortName: string | null;
    name: string;
    zones: Array<{
      id: number;
      shortName: string | null;
      name: string;
    }>;
  }>;
  interventions: Array<{ performanceId: number; zoneId: number }>;
};

type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: MatrixData };

const cellKey = (performanceId: number, zoneId: number) =>
  `${performanceId}:${zoneId}`;

export function TravauxZoning() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<PageState>({ status: "loading" });

  useEffect(() => {
    if (isNil(yardId)) {
      setState({ status: "error" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/intervention-matrix", {
        params: { path: { yardId } },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [yardId]);

  if (state.status === "loading") {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-500">
        Chargement des travaux et du zoning...
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-red-600">
        Erreur lors du chargement des travaux et du zoning
      </div>
    );
  }

  return <TravauxZoningMatrix data={state.data} />;
}

type TravauxZoningMatrixProps = { data: MatrixData };

function TravauxZoningMatrix({ data }: TravauxZoningMatrixProps) {
  const zones = data.buildings.flatMap((building) => building.zones);
  const hasPerformances = data.lots.some((lot) => lot.performances.length > 0);
  const interventionKeys = useMemo(
    () =>
      new Set(
        data.interventions.map((intervention) =>
          cellKey(intervention.performanceId, intervention.zoneId),
        ),
      ),
    [data.interventions],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-6">
      <header className="shrink-0">
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Travaux et zoning
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Les cellules bleues indiquent les prestations localisées dans chaque
          zone.
        </p>
      </header>

      {!hasPerformances || zones.length === 0 ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-zinc-200 bg-white p-6 text-sm text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900">
          Aucun travaux ou zoning n’a encore été défini pour ce chantier.
        </div>
      ) : (
        <div className="isolate min-h-0 flex-1 overflow-auto rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
          <table
            className="table-fixed border-separate border-spacing-0 text-xs"
            style={{ width: `${224 + zones.length * 64}px` }}
          >
            <colgroup>
              <col style={{ width: "224px" }} />
              {zones.map((zone) => (
                <col key={zone.id} style={{ width: "64px" }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  className="sticky left-0 top-0 z-30 h-9 truncate border-b border-r border-zinc-200 bg-zinc-50 p-2 text-left dark:border-zinc-800 dark:bg-zinc-900"
                  rowSpan={2}
                  title="Lot / Prestation"
                >
                  Lot / Prestation
                </th>
                {data.buildings
                  .filter((building) => building.zones.length > 0)
                  .map((building) => (
                    <th
                      key={building.id}
                      colSpan={building.zones.length}
                      className="sticky top-0 z-20 h-9 truncate border-b border-l border-zinc-200 bg-zinc-50 p-2 text-center font-medium dark:border-zinc-800 dark:bg-zinc-900"
                      title={building.name}
                    >
                      {building.shortName ?? building.name}
                    </th>
                  ))}
              </tr>
              <tr>
                {zones.map((zone) => (
                  <th
                    key={zone.id}
                    className="sticky top-9 z-20 h-24 border-b border-l border-zinc-200 bg-zinc-50 p-1 text-center font-normal text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900"
                    title={zone.name}
                  >
                    <span
                      className="inline-block max-h-20 overflow-hidden text-ellipsis whitespace-nowrap"
                      style={{ writingMode: "vertical-rl" }}
                    >
                      {zone.shortName ?? zone.name}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lots.map((lot) => {
                const lotLabel = [lot.nLot, lot.shortName ?? lot.name]
                  .filter(isNotNil)
                  .join(" — ");
                return (
                  <Fragment key={lot.id}>
                    <tr>
                      <th
                        className="sticky left-0 z-10 h-8 truncate border-b border-r border-zinc-200 bg-zinc-100 px-2 text-left font-semibold text-zinc-700 dark:border-zinc-800 dark:bg-zinc-800 dark:text-zinc-300"
                        title={lot.name}
                      >
                        {lotLabel}
                      </th>
                      <td
                        colSpan={zones.length}
                        className="h-8 border-b border-zinc-200 bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-800"
                      />
                    </tr>
                    {lot.performances.map((performance) => (
                      <tr key={performance.id}>
                        <th
                          className="sticky left-0 z-10 h-9 truncate border-b border-r border-zinc-200 bg-white px-2 text-left font-normal text-zinc-700 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-300"
                          title={performance.name}
                        >
                          {performance.name}
                        </th>
                        {zones.map((zone) => {
                          const hasIntervention = interventionKeys.has(
                            cellKey(performance.id, zone.id),
                          );
                          const label = `${performance.name} — ${zone.name} : ${
                            hasIntervention
                              ? "prestation localisée"
                              : "aucune prestation localisée"
                          }`;
                          return (
                            <td
                              key={zone.id}
                              aria-label={label}
                              title={label}
                              className="h-9 border-b border-l border-zinc-200 text-center dark:border-zinc-800"
                            >
                              {hasIntervention && (
                                <span
                                  aria-hidden="true"
                                  className="mx-auto block size-3 rounded-full bg-blue-600 dark:bg-blue-400"
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
